export interface SkillItem {
  name: string;
  category: string;
}

export interface ProjectItem {
  title: string;
  description: string;
  tags: string[];
}

export interface Section {
  id: string;
  title: string;
  icon: string;
  color: string;
  position: [number, number, number];
  content: SectionContent;
}

export interface SectionContent {
  heading: string;
  description?: string;
  skills?: SkillItem[];
  projects?: ProjectItem[];
  experience?: ExperienceItem[];
  education?: EducationItem[];
  certifications?: string[];
  contact?: ContactInfo;
}

export interface ExperienceItem {
  role: string;
  company: string;
  period: string;
  subtitle: string;
  description: string;
  bullets?: string[];
}

export interface EducationItem {
  degree: string;
  institution: string;
  year: string;
}

export interface ContactInfo {
  email: string;
  github: string;
  linkedin: string;
}

const PHI = (1 + Math.sqrt(5)) / 2;

function fibonacciSphere(index: number, total: number, radius: number): [number, number, number] {
  const y = 1 - (index / (total - 1)) * 2;
  const radiusAtY = Math.sqrt(1 - y * y);
  const theta = 2 * Math.PI * index / PHI;
  return [
    Math.cos(theta) * radiusAtY * radius,
    y * radius,
    Math.sin(theta) * radiusAtY * radius,
  ];
}

export const sections: Section[] = [
  {
    id: 'about',
    title: 'About Me',
    icon: '👤',
    color: '#00f0ff',
    position: fibonacciSphere(0, 11, 2.2),
    content: {
      heading: 'About Me',
      description:
        'DevOps Engineer with 3+ years of enterprise software development and Linux/automation experience, with hands-on depth in Kubernetes, Docker, GitLab CI/CD, Terraform, Prometheus, and Grafana.\n\nExperienced in supporting production-critical systems, writing Python and Bash automation, managing containerised deployments, and implementing GitOps workflows across development and production environments. Strong foundation in databases (PostgreSQL, MySQL), REST APIs, and scripting — with growing practical experience in Helm-based deployments and cloud-native infrastructure patterns.\n\nA genuine problem-solver who takes ownership of outcomes, communicates clearly across distributed teams, and thrives in collaborative, autonomous remote environments.',
    },
  },
  {
    id: 'skills',
    title: 'Skills',
    icon: '⚡',
    color: '#4060ff',
    position: fibonacciSphere(1, 11, 2.2),
    content: {
      heading: 'Core Technical Skills',
      skills: [
        { name: 'Kubernetes', category: 'Containers & Orchestration' },
        { name: 'Docker', category: 'Containers & Orchestration' },
        { name: 'Docker Compose', category: 'Containers & Orchestration' },
        { name: 'Rancher Desktop', category: 'Containers & Orchestration' },
        { name: 'Helm', category: 'Containers & Orchestration' },
        { name: 'ArgoCD Concepts', category: 'Containers & Orchestration' },
        { name: 'GitLab CI/CD', category: 'CI/CD & GitOps' },
        { name: 'GitHub Actions', category: 'CI/CD & GitOps' },
        { name: 'Jenkins', category: 'CI/CD & GitOps' },
        { name: 'GitOps Workflows', category: 'CI/CD & GitOps' },
        { name: 'Nexus Artifact Mgmt', category: 'CI/CD & GitOps' },
        { name: 'Terraform', category: 'Infrastructure as Code' },
        { name: 'Ansible Awareness', category: 'Infrastructure as Code' },
        { name: 'AWS (EC2, VPC, IAM, S3)', category: 'Infrastructure as Code' },
        { name: 'Prometheus', category: 'Monitoring & Observability' },
        { name: 'Grafana', category: 'Monitoring & Observability' },
        { name: 'Structured Logging', category: 'Monitoring & Observability' },
        { name: 'ELK Awareness', category: 'Monitoring & Observability' },
        { name: 'PostgreSQL', category: 'Databases' },
        { name: 'MySQL', category: 'Databases' },
        { name: 'SQLite', category: 'Databases' },
        { name: 'Redis & MongoDB', category: 'Databases' },
        { name: 'Python', category: 'Scripting & Automation' },
        { name: 'Bash', category: 'Scripting & Automation' },
        { name: 'Java', category: 'Scripting & Automation' },
        { name: 'Node.js Awareness', category: 'Scripting & Automation' },
        { name: 'Ubuntu / Debian', category: 'Linux & Systems' },
        { name: 'RHEL Concepts', category: 'Linux & Systems' },
        { name: 'SSH & Networking', category: 'Linux & Systems' },
        { name: 'systemctl & Services', category: 'Linux & Systems' },
        { name: 'Nginx Reverse Proxy', category: 'Networking & Security' },
        { name: 'IAM & Access Control', category: 'Networking & Security' },
        { name: 'Firewall Policy', category: 'Networking & Security' },
        { name: 'Jira & Agile/SDLC', category: 'Collaboration' },
        { name: 'Documentation', category: 'Collaboration' },
        { name: 'Remote Team Delivery', category: 'Collaboration' },
      ],
    },
  },
  {
    id: 'devops-cloud',
    title: 'DevOps & Cloud',
    icon: '☁️',
    color: '#8040ff',
    position: fibonacciSphere(2, 11, 2.2),
    content: {
      heading: 'DevOps & Cloud Projects',
      projects: [
        {
          title: 'Kubernetes & Cloud HomeLab',
          description:
            'Hands-on lab environment for practising platform engineering and GitOps skills end-to-end. Deployed and managed Kubernetes workloads using YAML manifests — Deployments, Services, ConfigMaps, Namespaces — with health checks and availability configurations in a Rancher Desktop mini-cluster. Applied Terraform to provision cloud infrastructure with reusable modules. Implemented GitOps workflow patterns with structured branching and environment promotion strategies.',
          tags: ['Kubernetes', 'Terraform', 'GitOps', 'Rancher Desktop', 'Linux'],
        },
      ],
    },
  },
  {
    id: 'kubernetes',
    title: 'K8s & Monitoring',
    icon: '🎯',
    color: '#ff40c0',
    position: fibonacciSphere(3, 11, 2.2),
    content: {
      heading: 'Kubernetes & Monitoring',
      projects: [
        {
          title: 'Prometheus & Grafana Monitoring',
          description:
            'Configured Prometheus scraping and Grafana dashboards for containerised services — alerting rules, health check endpoints, and structured observability for multi-container deployments. Implemented metrics collection, built dashboard authoring, defined alert rules, and wrote runbook-style documentation for operational reliability.',
          tags: ['Prometheus', 'Grafana', 'Alerting', 'Runbooks', 'Observability'],
        },
        {
          title: 'Kubernetes Cluster Management',
          description:
            'Deploying and managing containerised workloads on Kubernetes — writing YAML manifests for Deployments, Services, ConfigMaps and Namespaces. Managing pod lifecycle, health checks and availability in mini-cluster environments.',
          tags: ['Kubernetes', 'YAML', 'Health Checks', 'Namespaces'],
        },
      ],
    },
  },
  {
    id: 'cicd',
    title: 'CI/CD',
    icon: '🔄',
    color: '#00ff80',
    position: fibonacciSphere(4, 11, 2.2),
    content: {
      heading: 'CI/CD & DevOps Automation',
      projects: [
        {
          title: 'CI/CD and DevOps Automation Labs',
          description:
            'Implemented end-to-end CI/CD pipelines using GitHub Actions and GitLab CI/CD — integrating automated build, test, and deployment stages with YAML configuration and GitOps-aligned release workflows. Built containerised multi-service environments with Docker Compose. Managed artifact repository workflows with Nexus — pushing/pulling versioned images and maintaining release management practices.',
          tags: ['GitHub Actions', 'GitLab CI/CD', 'Docker Compose', 'Nexus', 'GitOps'],
        },
        {
          title: 'Jenkins Pipeline Automation',
          description:
            'Building and iterating CI/CD pipelines with Jenkins — automating build, test, and deployment stages with YAML configuration and Python-based validation scripts. Applying GitOps patterns for infrastructure change management.',
          tags: ['Jenkins', 'Python', 'GitOps', 'Docker'],
        },
      ],
    },
  },
  {
    id: 'fullstack',
    title: 'Full-Stack',
    icon: '🚀',
    color: '#ffaa00',
    position: fibonacciSphere(5, 11, 2.2),
    content: {
      heading: 'Full-Stack & Automation Projects',
      projects: [
        {
          title: 'Kai Konane — Educational Platform',
          description:
            'Full-stack web application for preschool learners with four user types: children, parents, teachers, administrators. Designed and built back-end REST API routes, SQLAlchemy ORM models, and PostgreSQL schema with role-based access control. Deployed and tested across containerised Ubuntu environments using Docker and Docker Compose. Developed and executed unit and system test cases throughout the development lifecycle.',
          tags: ['Python', 'Flask', 'PostgreSQL', 'Docker', 'REST API'],
        },
        {
          title: 'XML-Driven Document Automation Pipeline',
          description:
            'Production ETL automation solution built for and deployed at Baraka IT Solutions. Built a modular Python pipeline that reads structured XML input to drive file routing, folder creation, and document upload workflows — with clear separation of responsibilities and full operational documentation. Resolved edge cases in XML parsing, permissions, and file handling.',
          tags: ['Python', 'Bash', 'Linux', 'ETL', 'XML'],
        },
      ],
    },
  },
  {
    id: 'sap',
    title: 'SAP & Enterprise',
    icon: '🏢',
    color: '#ff6040',
    position: fibonacciSphere(6, 11, 2.2),
    content: {
      heading: 'SAP & Enterprise Work',
      projects: [
        {
          title: 'SAP Production Support & Automation',
          description:
            'Supported production-critical systems through structured incident triage — investigating issues across application logic, file handling, integrations and OS layers. Documented findings in knowledge-base entries for team use. Developed Python and Bash automation scripts for file upload pipelines, XML-driven routing, and folder structure management — reducing manual operational work.',
          tags: ['SAP', 'Python', 'Bash', 'Production Support', 'Incident Triage'],
        },
        {
          title: 'Linux Environment Administration',
          description:
            'Configured and maintained Linux environments across development, test and production — applying change control processes and resolving daemon errors, permission issues, package conflicts, and repository misconfigurations.',
          tags: ['Linux', 'Change Control', 'Troubleshooting', 'Dev/Test/Prod'],
        },
      ],
    },
  },
  {
    id: 'experience',
    title: 'Experience',
    icon: '💼',
    color: '#40c0ff',
    position: fibonacciSphere(7, 11, 2.2),
    content: {
      heading: 'Work Experience',
      experience: [
        {
          role: 'DevOps Engineer — Internship',
          company: 'KubeCraft',
          period: 'Jun 2025 – Present',
          subtitle: 'Kubernetes, CI/CD, Infrastructure as Code, Observability',
          description:
            'Practical DevOps internship focused on Kubernetes, CI/CD, Infrastructure as Code, observability and cloud infrastructure — all hands-on in lab and cloud environments.',
          bullets: [
            'Deploying and managing containerised workloads on Kubernetes — YAML manifests, pod lifecycle, health checks in Rancher Desktop',
            'Building CI/CD pipelines with GitHub Actions and Jenkins — GitOps patterns for infrastructure change management',
            'Provisioning cloud infrastructure using Terraform — reusable modules for VPCs, compute and networking',
            'Implementing Prometheus-based monitoring — Grafana dashboards, alert rules, runbook documentation',
            'Administering Linux lab environments — SSH, firewall rules, log analysis, service troubleshooting',
          ],
        },
        {
          role: 'SAP / Software Developer',
          company: 'Baraka IT Solutions',
          period: 'Mar 2023 – Present',
          subtitle: 'Automation Engineering, Back-end Development, Production Support',
          description:
            'Full-stack development and production support in an enterprise SAP and Linux environment — automation engineering, back-end development, change management, and cross-functional delivery.',
          bullets: [
            'Developed Python and Bash automation scripts for file upload pipelines, XML-driven routing, and folder structure management',
            'Built and supported a production XML-driven ETL workflow with modular design and full documentation',
            'Configured and maintained Linux environments across dev, test and production with change control processes',
            'Supported production-critical systems through structured incident triage across application logic, file handling, integrations and OS layers',
            'Collaborated with developers, technical leads, and business stakeholders to integrate automation into existing workflows',
          ],
        },
      ],
    },
  },
  {
    id: 'education',
    title: 'Education',
    icon: '🎓',
    color: '#a080ff',
    position: fibonacciSphere(8, 11, 2.2),
    content: {
      heading: 'Education & Certifications',
      education: [
        {
          degree: 'Advanced Diploma in Computer Sciences — NQF Level 7',
          institution: 'Tshwane University of Technology',
          year: '2024',
        },
        {
          degree: 'National Diploma in IT: Software Development — NQF Level 6',
          institution: 'Tshwane University of Technology',
          year: '2022',
        },
      ],
      certifications: [
        'IBM Applied DevOps Engineering Certificate — GitLab CI/CD, Docker, Kubernetes, automated testing, deployment pipelines',
        'IBM DevOps and Software Engineering Certificate — Agile, CI/CD, containerisation, cloud-native concepts, deployment workflows',
      ],
    },
  },
  {
    id: 'contact',
    title: 'Contact',
    icon: '📬',
    color: '#00f0ff',
    position: fibonacciSphere(9, 11, 2.2),
    content: {
      heading: 'Get In Touch',
      description: "I'm always open to new opportunities, collaborations, and conversations about DevOps, Cloud, and Platform Engineering. Let's connect!",
      contact: {
        email: 'lsktheledi@gmail.com',
        github: 'https://github.com/nolotheledi',
        linkedin: 'https://linkedin.com/in/nolotheledi',
      },
    },
  },
];

export const categoryColors: Record<string, string> = {
  'Containers & Orchestration': '#8040ff',
  'CI/CD & GitOps': '#00ff80',
  'Infrastructure as Code': '#ff40c0',
  'Monitoring & Observability': '#ffaa00',
  'Databases': '#4060ff',
  'Scripting & Automation': '#ff6040',
  'Linux & Systems': '#00f0ff',
  'Networking & Security': '#40c0ff',
  'Collaboration': '#a080ff',
};
